import jsonExport from 'jsonexport/dist';
import { Fragment, memo, useState } from 'react';
import Button from '@mui/material/Button';
import { useRefresh, useNotify, useDataProvider, Confirm, BulkDeleteButtonProps, BulkExportButton, BulkExportButtonProps, Datagrid, DateField, downloadCSV, List, TextField, TextInput, useRecordContext, EditButton } from 'react-admin';
import Chip from '@mui/material/Chip';

const AllowedRolesField: React.FC<{ label: string }> = () => {
  const record = useRecordContext();

  if (!record?.allowedRoles?.length) return <span>*</span>;

  return record.allowedRoles.map((role: string) => <Chip key={`${role}-${record.id}`} label={role} />);
};

const DashboardsListBulkActions = memo((props: BulkDeleteButtonProps & BulkExportButtonProps) => (
  <Fragment>
    <BulkExportButton {...props} />
  </Fragment>
));

const exporter = (users: any[]) => {
  return jsonExport(users, (err: Error, csv: string) => downloadCSV(csv, 'dashboards'));
};

const SyncDashboardsButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isOpen, setOpen] = useState(false);

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => {
    dataProvider
      .syncDashboards()
      .then(() => {
        setOpen(false);
        refresh();
        notify('Dashboards successfully synced', { type: 'success' });
      })
      .catch((error: any) => {
        console.error('Error syncing dashboards:', error);
        notify('Error syncing dashboards', { type: 'error' });
      });
  };

  return (
    <>
      <Button onClick={handleClick}>Sync Dashboards</Button>
      <Confirm isOpen={isOpen} title="Sync Dashboards" content="Are you sure you want to sync dashboards? It is not a reversible action!" onConfirm={handleConfirm} onClose={handleDialogClose} />
    </>
  );
};

const SyncDataCubesButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isOpen, setOpen] = useState(false);

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => {
    dataProvider
      .syncDataCubes()
      .then(() => {
        setOpen(false);
        refresh();
        notify('Data Cubes successfully synced', { type: 'success' });
      })
      .catch((error: any) => {
        console.error('Error syncing Data Cubes:', error);
        notify('Error syncing Data Cubes', { type: 'error' });
      });
  };

  return (
    <>
      <Button onClick={handleClick}>Sync Data Cubes</Button>
      <Confirm isOpen={isOpen} title="Sync Data Cubes" content="Are you sure you want to sync Data Cubes? It is not a reversible action!" onConfirm={handleConfirm} onClose={handleDialogClose} />
    </>
  );
};

const DashboardsListActions = (props: any) => (
  <div {...props}>
    <SyncDashboardsButton />
    <SyncDataCubesButton />
    <BulkExportButton {...props} />
  </div>
);

const dashboardsFilter = [<TextInput label="Name" source="name" alwaysOn />, <TextInput label="Uuid" source="uuid" alwaysOn />];

const DashboardsList: React.FC = () => {
  return (
    <List actions={<DashboardsListActions />} empty={false} filters={dashboardsFilter} sort={{ field: 'id', order: 'DESC' }} exporter={exporter}>
      <Datagrid bulkActionButtons={<DashboardsListBulkActions />} optimized sx={{ image: { maxHeight: '1rem' } }}>
        <TextField source="id" />
        <TextField source="uuid" />
        <TextField source="name" />
        <TextField source="externalId" />
        <AllowedRolesField label="Allowed Roles" />
        <DateField source="createdAt" sortByOrder="DESC" />
        <DateField source="updatedAt" sortByOrder="DESC" />
        <EditButton />
      </Datagrid>
    </List>
  );
};

export default DashboardsList;
